import apiClient from './apiClient';

export interface ClinicLabIntegration {
  id: string;
  clinic_id: string;
  lab_template_id: string;
  inbound_email_address: string | null;
  allowed_sender_emails: string[];
  mapping_priority: string[];
  visit_match_days: number;
  notify_medic_on_received: boolean;
  notify_client_optional: boolean;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ClinicLabIntegrationCreate {
  lab_template_id?: string;
  allowed_sender_emails?: string[];
  mapping_priority?: string[];
  visit_match_days?: number;
  notify_medic_on_received?: boolean;
  notify_client_optional?: boolean;
  active?: boolean;
}

export async function listClinicLabIntegrations(clinicId?: string): Promise<ClinicLabIntegration[]> {
  const params = clinicId ? { clinicId } : {};
  const res = await apiClient.get<{ success: boolean; data: ClinicLabIntegration[] }>('/clinic/lab-integrations', { params });
  return res.data.data || [];
}

export async function getClinicLabIntegration(id: string): Promise<ClinicLabIntegration | null> {
  const res = await apiClient.get<{ success: boolean; data: ClinicLabIntegration }>(`/clinic/lab-integrations/${id}`);
  return res.data.data || null;
}

export async function createClinicLabIntegration(data: ClinicLabIntegrationCreate, clinicId?: string): Promise<ClinicLabIntegration> {
  const body = clinicId ? { ...data, clinic_id: clinicId } : data;
  const res = await apiClient.post<{ success: boolean; data: ClinicLabIntegration }>('/clinic/lab-integrations', body);
  return res.data.data!;
}

export async function updateClinicLabIntegration(id: string, data: Partial<ClinicLabIntegrationCreate>): Promise<ClinicLabIntegration> {
  const res = await apiClient.put<{ success: boolean; data: ClinicLabIntegration }>(`/clinic/lab-integrations/${id}`, data);
  return res.data.data!;
}

export interface TestIntegrationResult {
  parsed: Record<string, string>;
  resolved: { patientId: string; visitId: string | null } | null;
  pdfUploaded?: { key: string; url: string };
  message?: string;
}

export async function testClinicLabIntegration(
  id: string,
  file?: File,
  subject?: string,
  body?: string
): Promise<TestIntegrationResult> {
  const formData = new FormData();
  if (file) formData.append('pdf', file);
  if (subject) formData.append('subject', subject);
  if (body) formData.append('body', body);
  const res = await apiClient.post<{ success: boolean; data: TestIntegrationResult }>(`/clinic/lab-integrations/${id}/test`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.data!;
}

export async function getClinicLabIntegrationLogs(
  id: string,
  limit?: number,
  offset?: number
): Promise<{ id: string; event_type: string; payload: unknown; created_at: string }[]> {
  const params: Record<string, string | number> = {};
  if (limit != null) params.limit = limit;
  if (offset != null) params.offset = offset;
  const res = await apiClient.get<{ success: boolean; data: unknown[] }>(`/clinic/lab-integrations/${id}/logs`, { params });
  return (res.data.data || []) as { id: string; event_type: string; payload: unknown; created_at: string }[];
}

export async function listUnmatchedLabResults(clinicId?: string, limit?: number, offset?: number): Promise<{ data: unknown[]; total: number }> {
  const params: Record<string, string | number> = {};
  if (clinicId) params.clinicId = clinicId;
  if (limit != null) params.limit = limit;
  if (offset != null) params.offset = offset;
  const res = await apiClient.get<{ success: boolean; data: unknown[]; total: number }>('/clinic/unmatched-lab-results', { params });
  return { data: res.data.data || [], total: res.data.total || 0 };
}
